// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { Fragment } from "react";
import styled, { css } from "styled-components";

interface Props {
    categories: AutocompleteItemModel[] | null;
    popularCategories?: AutocompleteItemModel[] | null;
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteCategoriesStyles;
    isRetailSearchCompletionResults?: boolean;
    // <XNG-Changes>
    searchQuery?: string;
}

export interface AutocompleteCategoriesStyles {
    subHeaderText?: TypographyPresentationProps;
    divider?: InjectableCss;
    categoryItemText?: TypographyPresentationProps;
    focusedCategoryItemText?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    focusedLink?: LinkPresentationProps;
    autocompleteCategoriesRowWrapper?: InjectableCss;
    autocompleteCategoriesColumnWrapper?: InjectableCss;
}

export const autocompleteCategoriesStyles: AutocompleteCategoriesStyles = {
    categoryItemText: {
        ellipsis: true,
        css: css`
            width: 100%;
            text-align: left;
            cursor: pointer;
        `,
    },
    subHeaderText: {
        weight: 600,
        css: css`
            text-align: left;
            margin: 0 0 5px 0;
        `,
    },
    focusedCategoryItemText: {
        ellipsis: true,
        css: css`
            width: 100%;
            text-align: left;
            cursor: pointer;
            background-color: lightgray;
        `,
    },
    link: {
        typographyProps: {
            ellipsis: true,
            size: 12,
            lineHeight: "18px",
            css: css`
                width: 100%;
                text-align: left;
                margin: 5px 0;
            `,
        },
        css: css`
            width: auto;
        `,
    },
    focusedLink: {
        typographyProps: {
            ellipsis: true,
            size: 12,
            lineHeight: "18px",
            css: css`
                width: 100%;
                text-align: left;
                margin: 5px 0;
            `,
        },
        css: css`
            background-color: lightgray;
        `,
    },
    divider: {
        css: css`
            min-width: 100%;
        `,
    },
    autocompleteCategoriesRowWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
        `,
    },
    autocompleteCategoriesColumnWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 375px;
        `,
    },
};

const styles = autocompleteCategoriesStyles;

const Divider = styled.hr`
    ${styles.divider?.css}
`;

// <XNG-Changes>
const categoryTextFormat = (category: string, query = "") => {
    if (category.toLowerCase().indexOf(query.toLowerCase()) === -1) {
        return category;
    }
    const queryIndex = category.toLowerCase().indexOf(query.toLowerCase());
    const beforeQuery = category.substring(0, queryIndex);
    const afterQuery = category.substring(queryIndex + query.length);

    return (
        <>
            {beforeQuery}
            <Typography
                css={css`
                    font-weight: bold;
                    letter-spacing: inherit;
                    line-height: inherit;
                `}
            >
                {category.substring(queryIndex, queryIndex + query.length)}
            </Typography>
            {afterQuery}
        </>
    );
};
// </XNG-Changes>

class AutocompleteCategories extends React.Component<Props> {
    private readonly styles: AutocompleteCategoriesStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    renderCategories = (categories: AutocompleteItemModel[]) => {
        const styles = this.styles;
        return (
            <>
                {categories.map(category => (
                    <Fragment key={`${category.id}`}>
                        {category.subtitle && (
                            <Link
                                {...(this.props.focusedItem === category ? styles.focusedLink : styles.link)}
                                onClick={() => {
                                    this.props.goToUrl(category.url);
                                }}
                            >
                                {/* <XNG-Changes> */}
                                {categoryTextFormat(category.title, this.props.searchQuery)}
                                <>{` ${translate("in")} ${category.subtitle}`}</>
                                {/* </XNG-Changes> */}
                            </Link>
                        )}
                        {!category.subtitle && (
                            <Link
                                {...(this.props.focusedItem === category ? styles.focusedLink : styles.link)}
                                onClick={() => {
                                    this.props.goToUrl(category.url);
                                }}
                            >
                                {categoryTextFormat(category.title, this.props.searchQuery)}
                            </Link>
                        )}
                    </Fragment>
                ))}
            </>
        );
    };
    render() {
        const { categories, popularCategories, isRetailSearchCompletionResults } = this.props;
        if ((!categories || categories.length === 0) && (!popularCategories || popularCategories.length === 0)) {
            return null;
        }
        const styles = this.styles;
        return (
            <>
                {/* <Typography
                    {...styles.headerText}
                    data-test-selector="autocompleteCategories_Header"
                    className="autocomplete-categories-header"
                >
                    {translate("Categories")}
                </Typography> */}
                {categories &&
                    categories.length > 0 &&
                    !isRetailSearchCompletionResults &&
                    this.renderCategories(categories)}
                {isRetailSearchCompletionResults && (
                    <>
                        <Divider />
                        <StyledWrapper
                            {...this.styles.autocompleteCategoriesRowWrapper}
                            className="autocomplete-categories-row-wrapper"
                        >
                            {popularCategories && popularCategories.length > 0 && (
                                <StyledWrapper
                                    {...this.styles.autocompleteCategoriesColumnWrapper}
                                    className="autocomplete-categories-column-wrapper"
                                >
                                    <Typography
                                        {...styles.subHeaderText}
                                        data-test-selector="autocompleteCategories_subHeader"
                                        className="autocomplete-categories-subheader"
                                    >
                                        {translate("popular")}
                                    </Typography>
                                    {this.renderCategories(popularCategories)}
                                </StyledWrapper>
                            )}
                            {categories && categories.length > 0 && (
                                <StyledWrapper
                                    {...this.styles.autocompleteCategoriesColumnWrapper}
                                    className="autocomplete-categories-column-wrapper"
                                >
                                    <Typography
                                        {...styles.subHeaderText}
                                        data-test-selector="autocompleteCategories_subHeader"
                                        className="autocomplete-categories-subheader"
                                    >
                                        {translate("suggested")}
                                    </Typography>
                                    {this.renderCategories(categories)}
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    </>
                )}
            </>
        );
    }
}

export default AutocompleteCategories;
